import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Chip,
  MenuItem,
  Paper,
  makeStyles,
} from "@material-ui/core";
import {
  HydrovacScreeningQuestions as screeningQuestions,
  HydrovacDistanceOptions as distanceOptions,
  HydrovacYesNoOptions,
} from "../../utils/constants";

import { HydrovacTicket } from "../../interface/api/HydrovacTicket";
import { hydrovacTicketNumber } from "../../utils/parser";
import hydrovacTicketService from "../../services/hydrovacTicketService";
import { useTransportData } from "../../hooks/useTransportData";
import FormSelectSearchField from "../shared/form-select-search-field";
import FormDateTimePicker from "../shared/form-date-time-picker";
import FormTruckLicensePlateField from "../shared/form-truck-license-plate-field";
import { isLicensePlateMissing } from "../../utils/ticket-helpers";
import SignatureCanvas from "react-signature-canvas";
import { SoilFLOIcon } from "../shared/icons";
import { hasBeenDispatched } from "../../utils/dispatch-state";
import TicketLoading from "../shared/ticket-loading";
import PageHero from "../shared/page-hero";
import HydrovacPrintableTicket from "./hydrovac-printable-ticket";

const useStyles = makeStyles({
  sectionHeader: {
    backgroundColor: "#4F4F4F",
    color: "#fff",
    textAlign: "center",
    padding: "8px",
    marginBottom: 16,
  },
  questionRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "4px 0",
    borderBottom: "1px solid #eee",
  },
  fieldBox: {
    border: "1px solid #333",
    padding: "8px",
    textAlign: "center",
    minHeight: 60,
    height: "100%",
    boxSizing: "border-box" as const,
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "center",
    overflowWrap: "anywhere" as const,
  },
  fieldBoxLabel: {
    fontSize: "0.7rem",
    fontWeight: 600,
    marginBottom: 4,
  },
  fieldBoxValue: {
    fontSize: "0.85rem",
    minHeight: 20,
  },
  fieldBoxSub: {
    fontSize: "0.65rem",
    color: "#666",
    marginTop: 4,
  },
  signatureContainer: {
    border: "1px solid #ccc",
    borderRadius: 4,
    position: "relative" as const,
  },
  signatureCanvas: {
    width: "100%",
    height: 150,
    display: "block",
  },
  signatureClear: {
    position: "absolute" as const,
    top: 4,
    right: 4,
    fontSize: "0.75rem",
    padding: "2px 8px",
    minWidth: "auto",
  },
  signatureImage: {
    maxWidth: "100%",
    maxHeight: 150,
    display: "block",
  },
});

/**
 * Hydrovac dispatch form for anonymous drivers arriving from a ticket QR code -
 * the hydrovac counterpart to ticket-short-view.tsx. Keeps its own form rather
 * than sharing sub-stream-driver-form.tsx: hydrovac collects screening and
 * inspection answers plus a signature that the other streams have no concept of.
 */
const HydrovacDriverForm = () => {
  const classes = useStyles();
  const { id }: any = useParams();
  const [ticket, setTicket] = useState<HydrovacTicket | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [alreadyDispatched, setAlreadyDispatched] = useState(false);
  const [formAttempted, setFormAttempted] = useState(false);
  const { contractors } = useTransportData({
    contractorFilter: "isHydrovac",
    loadTrucks: false,
  });
  const sigCanvasRef = useRef<SignatureCanvas>(null);

  // Load existing signature into canvas when ticket data arrives
  useEffect(() => {
    if (ticket?.driverSignature && sigCanvasRef.current) {
      sigCanvasRef.current.fromDataURL(ticket.driverSignature);
    }
  }, [ticket?.id]);

  useMemo(() => {
    hydrovacTicketService.getTicket(id).then((response) => {
      if (!response.ok) {
        toast.error("Failed to load ticket");
        return;
      }
      const t = response.data as HydrovacTicket;
      // The signature is the driver-supplied half of the rule here; it is
      // required to submit. Read before the prefill below.
      setAlreadyDispatched(hasBeenDispatched(t.timeLoaded, t.driverSignature));
      // Default Time Loaded to now, but never overwrite what the ticket carries.
      if (!t.isReceived && !t.timeLoaded) {
        t.timeLoaded = new Date() as any;
      }
      setTicket(t);
    });
  }, []);

  const updateField = (field: keyof HydrovacTicket, value: any) => {
    if (!ticket) return;
    setTicket({ ...ticket, [field]: value });
  };

  const driverRequiredFields: (keyof HydrovacTicket)[] = [
    "propertyAddress",
    "landUse",
    "descriptionOfActivity",
    "shippingCompany",
    "shippingDriver",
    "truckLicensePlate",
    "contractorCompanyId",
    "timeLoaded",
    "driverSignature",
  ];

  const driverRequiredScreening = screeningQuestions.map((q) => q.field);

  const isDriverFieldEmpty = (field: string) => {
    if (!ticket) return true;
    const val = (ticket as any)[field];
    if (field === "truckLicensePlate") return isLicensePlateMissing(val);
    if (field === "shippingDriver") return !val?.trim();
    return val === null || val === undefined || val === "";
  };

  const onDriverSubmit = async () => {
    if (!ticket) return;
    setFormAttempted(true);
    const missingFields = driverRequiredFields.some((f) => isDriverFieldEmpty(f));
    const missingScreening = driverRequiredScreening.some((f) => isDriverFieldEmpty(f));
    if (missingFields || missingScreening) {
      toast.error("Please fill in all required fields");
      return;
    }
    const response = await hydrovacTicketService.driverSubmit(ticket.id, ticket);
    if (response.ok) {
      setTicket(response.data as HydrovacTicket);
      setSubmitted(true);
    } else {
      toast.error("Failed to submit ticket");
    }
  };

  if (ticket === null) return <TicketLoading />;


  // Already received: the driver is looking at a completed ticket, so show the
  // printed ticket rather than a form that can no longer submit anything. The QR
  // they arrived from carries the hash, so reuse it rather than asking the
  // server - the hash endpoint is staff-only.
  if (ticket.isReceived) {
    const hash = new URLSearchParams(window.location.search).get("hash");
    return (
      <HydrovacPrintableTicket
        ticket={ticket}
        ticketUrl={`${window.location.origin}/hydrovac/${ticket.id}${
          hash ? `?hash=${hash}` : ""
        }`}
      />
    );
  }

  // SoilFLO loads are managed externally - nothing here is ours to edit.
  if (ticket.isSoilFLOLoad) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            border: "2px solid black",
            padding: "32px 20px",
            marginTop: "20px",
            marginBottom: "20px",
            textAlign: "center",
          }}
          style={{ backgroundColor: "#fff" }}
        >
          <SoilFLOIcon />
          <Typography variant="h5" style={{ marginTop: 12, marginBottom: 12 }}>
            This ticket is managed in SoilFLO
          </Typography>
          <Typography color="textSecondary">
            Ticket #{hydrovacTicketNumber(ticket)}
          </Typography>
        </Box>
      </Container>
    );
  }


  // Same confirmation the main ticket shows after dispatch
  // (ticket-short-view.tsx:114). alreadyDispatched keeps it there across a
  // refresh, when the submitted flag has been reset.
  if (submitted || alreadyDispatched) {
    return (
      <Container>
        <PageHero
          title="Truck en Route"
          subtitle="You have successfully dispatched the truck."
          showBackButton={true}
        />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          border: "2px solid black",
          padding: "20px",
          marginTop: "20px",
          marginBottom: "20px",
        }}
        style={{ backgroundColor: "#fff" }}
      >
        {ticket.isReceived && (
          <Chip label="Received" color="primary" style={{ marginBottom: 12 }} />
        )}
        {/* Header */}
        <Box sx={{ textAlign: "right", marginBottom: 10 }}>
          <Typography variant="body2" style={{ display: "inline" }}>
            USM TICKET #{" "}
          </Typography>
          <Typography
            variant="h5"
            style={{ color: "red", display: "inline", fontWeight: 600 }}
          >
            {hydrovacTicketNumber(ticket)}
          </Typography>
        </Box>

        {/* Section 1: Source Information */}
        <Paper style={{ padding: 24, marginTop: 16 }}>
          <Typography variant="h6" gutterBottom>
            Source Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <>
                  <FormSelectSearchField
                    name="contractorCompanyId"
                    placeholder="Contractor *"
                    options={contractors}
                    value={
                      ticket.contractorCompanyId
                        ? contractors.find((c) => c.value === ticket.contractorCompanyId) ?? null
                        : null
                    }
                    onChange={(option: any) => {
                      if (option) {
                        setTicket({
                          ...ticket,
                          contractorCompanyId: option.value,
                          contractorCompanyName: option.label,
                        });
                      } else {
                        setTicket({
                          ...ticket,
                          contractorCompanyId: null,
                          contractorCompanyName: null,
                        });
                      }
                    }}
                    isClearable
                  />
                {formAttempted && isDriverFieldEmpty("contractorCompanyId") && (
                  <Typography variant="caption" color="error">Required</Typography>
                )}
              </>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Property Address"
                required
                error={formAttempted && isDriverFieldEmpty("propertyAddress")}
                helperText={
                  formAttempted && isDriverFieldEmpty("propertyAddress") ? "Required" : ""
                }
                value={ticket.propertyAddress ?? ""}
                onChange={(e) => updateField("propertyAddress", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                label="Description of Activity"
                required
                error={formAttempted && isDriverFieldEmpty("descriptionOfActivity")}
                helperText={formAttempted && isDriverFieldEmpty("descriptionOfActivity") ? "Required" : ""}
                value={ticket.descriptionOfActivity ?? ""}
                onChange={(e) => updateField("descriptionOfActivity", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                variant="outlined"
                label="Work Order #"
                value={ticket.workOrderNumber ?? ""}
                onChange={(e) => updateField("workOrderNumber", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                variant="outlined"
                label="Land Use"
                required
                error={formAttempted && isDriverFieldEmpty("landUse")}
                helperText={formAttempted && isDriverFieldEmpty("landUse") ? "Required" : ""}
                value={ticket.landUse ?? ""}
                onChange={(e) => updateField("landUse", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                variant="outlined"
                label="Property Owner"
                value={ticket.propertyOwner ?? ""}
                onChange={(e) => updateField("propertyOwner", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormDateTimePicker
                label="Time Loaded *"
                value={ticket.timeLoaded ? new Date(ticket.timeLoaded) : null}
                onChange={(date: any) =>
                  updateField("timeLoaded", date)
                }
                clearable
              />
              {formAttempted && isDriverFieldEmpty("timeLoaded") && (
                <Typography variant="caption" color="error">Required</Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Shipping Company"
                required
                error={formAttempted && isDriverFieldEmpty("shippingCompany")}
                helperText={formAttempted && isDriverFieldEmpty("shippingCompany") ? "Required" : ""}
                value={ticket.shippingCompany ?? ""}
                onChange={(e) => updateField("shippingCompany", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                variant="outlined"
                label="Shipping Driver"
                required
                error={formAttempted && isDriverFieldEmpty("shippingDriver")}
                helperText={formAttempted && isDriverFieldEmpty("shippingDriver") ? "Required" : ""}
                value={ticket.shippingDriver ?? ""}
                onChange={(e) => updateField("shippingDriver", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                variant="outlined"
                label="Truck No."
                value={ticket.truckNumber ?? ""}
                onChange={(e) => updateField("truckNumber", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormTruckLicensePlateField
                value={ticket.truckLicensePlate ?? ""}
                name="truckLicensePlate"
                label="Truck License Plate *"
                onChange={(e) =>
                  updateField("truckLicensePlate", e.target.value.toUpperCase())
                }
              />
              {formAttempted && isDriverFieldEmpty("truckLicensePlate") && (
                <Typography variant="caption" color="error">Required</Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 4 }}>
                Driver Signature *
              </Typography>
              {formAttempted && isDriverFieldEmpty("driverSignature") && (
                <Typography variant="caption" color="error" style={{ marginBottom: 4, display: "block" }}>
                  Signature is required
                </Typography>
              )}
              <Box className={classes.signatureContainer}>
                <SignatureCanvas
                  ref={sigCanvasRef}
                  penColor="black"
                  canvasProps={{ className: classes.signatureCanvas }}
                  onEnd={() => {
                    if (sigCanvasRef.current) {
                      updateField(
                        "driverSignature",
                        sigCanvasRef.current.toDataURL("image/png")
                      );
                    }
                  }}
                />
                <Button
                  size="small"
                  className={classes.signatureClear}
                  onClick={() => {
                    if (sigCanvasRef.current) {
                      sigCanvasRef.current.clear();
                      updateField("driverSignature", "");
                    }
                  }}
                >
                  Clear
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Section 2: Screening Questions */}
        <Paper style={{ padding: 24, marginTop: 16 }}>
          <Typography variant="h6" gutterBottom>
            Screening Questions
          </Typography>
          <Grid container spacing={3}>
            {screeningQuestions.map(({ field, label, type }) => {
              const options = type === "distance" ? distanceOptions : HydrovacYesNoOptions;
              return (
                <Grid item xs={12} sm={6} key={field}>
                  <Typography variant="body2" style={{ marginBottom: 4, fontWeight: 500 }}>
                    {label} <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    variant="outlined"
                    error={formAttempted && isDriverFieldEmpty(field)}
                    helperText={formAttempted && isDriverFieldEmpty(field) ? "Required" : ""}
                    value={(ticket as any)[field] ?? ""}
                    onChange={(e) =>
                      updateField(field as keyof HydrovacTicket, e.target.value)
                    }
                  >
                    <MenuItem value="">
                      <em>Select...</em>
                    </MenuItem>
                    {options.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              );
            })}
          </Grid>
        </Paper>

        {/* Submit */}
        <Box display="flex" justifyContent="flex-end" mt={2} mb={4}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={onDriverSubmit}
          >
            Submit Ticket
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default HydrovacDriverForm;
